
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { BrandLogo } from '@/components/ui/brand-logo';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type');

  useEffect(() => {
    const verifyEmail = async () => {
      // Handle Supabase native verification
      if (tokenHash && type) {
        setIsVerifying(true);
        try {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as any,
          });

          if (error) {
            console.error('Supabase verification error:', error);
            setError('Failed to verify email. The link may have expired.');
            return;
          }

        // Refresh session to update email verification status
        await supabase.auth.refreshSession();
        
        setIsVerified(true);
        toast.success('Email verified successfully! You can now access all features.');
        } catch (error: any) {
          console.error('Email verification error:', error);
          setError('An error occurred while verifying your email');
        } finally {
          setIsVerifying(false);
        }
        return;
      }

      // Handle custom token verification
      if (!token) {
        return;
      }

      setIsVerifying(true);
      try {
        const { data, error } = await supabase.rpc('verify_email_with_token', { p_token: token });
        if (error) {
          console.error('RPC error verifying email:', error);
          setError('An error occurred while verifying your email');
          return;
        }

        const res = data as any;
        if (!res || res.success !== true) {
          const code = res?.error || 'invalid_token';
          const msg = code === 'token_expired'
            ? 'This verification link has expired'
            : code === 'token_used'
            ? 'This verification link has already been used'
            : 'Invalid verification link';
          setError(msg);
          return;
        }

        // Refresh session to update email verification status
        await supabase.auth.refreshSession();
        
        setIsVerified(true);
        toast.success('Email verified successfully! You can now access all features.');
      } catch (error: any) {
        console.error('Email verification error:', error);
        setError('An error occurred while verifying your email');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token, tokenHash, type]);

  const handleContinue = async () => {
    try {
      // Refresh the session to get updated auth state
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error refreshing session:', error);
        toast.error('Please log in to continue');
        navigate('/login', { state: { message: 'Email verified! Please log in to continue.' } });
        return;
      }

      if (!session) {
        // No active session, redirect to login with success message
        toast.success('Email verified! Please log in to continue.');
        navigate('/login', { state: { message: 'Email verified! Please log in to continue.' } });
        return;
      }

      // We have a valid session, navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error during continue:', error);
      navigate('/login', { state: { message: 'Email verified! Please log in to continue.' } });
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      // Get current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user) {
        toast.error('Please log in to resend verification email.');
        navigate('/login');
        return;
      }

      const userId = session.user.id;
      const userEmail = email || session.user.email;
      
      if (!userEmail) {
        toast.error('Email address not found. Please sign up again.');
        navigate('/signup');
        return;
      }

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          email: userEmail,
          userId,
          type: 'email_verification'
        }
      });

      if (error) throw error;
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      console.error('Error resending verification:', error);
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <Helmet>
            <title>Verify Email | The PEO App</title>
            <meta name="description" content="Verify your email to activate your The PEO App account." />
            <link rel="canonical" href="/verify-email" />
          </Helmet>
          <div>
            <div className="flex justify-center">
              <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Verifying your email...
            </h2>
            <div className="mt-4 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Helmet>
            <title>Email Verification Failed | The PEO App</title>
            <meta name="description" content="We couldn't verify your email. Get a new link or log in to try again." />
            <link rel="canonical" href="/verify-email" />
          </Helmet>
          <div>
            <div className="flex justify-center">
              <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Email Verification Failed
            </h2>
            <p className="mt-2 text-center text-red-600">{error}</p>
          </div>
          <div className="mt-8 space-y-4">
            <Button
              onClick={handleResendVerification}
              className="w-full py-6"
            >
              Get New Verification Link
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full py-6"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Helmet>
            <title>Email Verified | The PEO App</title>
            <meta name="description" content="Your email has been verified. Continue to your dashboard to get started." />
            <link rel="canonical" href="/verify-email" />
          </Helmet>
          <div>
            <div className="flex justify-center">
              <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Email Verified Successfully!
            </h2>
            <p className="mt-2 text-center text-gray-600">
              Your email has been verified. You can now access all features of The PEO App.
            </p>
          </div>
          <Button
            onClick={handleContinue}
            className="w-full py-6"
          >
            Continue to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Show "check your email" message if no token present
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Helmet>
            <title>Verify Your Email | The PEO App</title>
            <meta name="description" content="Please check your email to verify your account." />
            <link rel="canonical" href="/verify-email" />
          </Helmet>
          <div>
            <div className="flex justify-center">
              <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-center text-gray-600">
              We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
            </p>
            <p className="mt-4 text-center text-sm text-gray-500">
              Didn't receive the email? Check your spam folder or click below to resend.
            </p>
          </div>
          <div className="space-y-4">
            <Button
              onClick={handleResendVerification}
              disabled={isResending}
              className="w-full py-6"
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full py-6"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VerifyEmail;
